import React from "react";
import { motion } from "framer-motion";
import {
  Brain,
  Sparkles,
  Layers,
  UserCheck,
  Workflow,
  Database,
  BarChart3,
  Languages,
  Smile,
  GraduationCap,
  Lightbulb,
  Plug,
  type LucideIcon,
} from "lucide-react";
import AnimatedSection from "./AnimatedSection";
import SectionHeader from "./SectionHeader";

type Feature = {
  title: string;
  description: string;
  icon: LucideIcon;
};

const features: Feature[] = [
  { title: "Natural Language Processing", description: "Understand intent and context in human-like conversations.", icon: Brain },
  { title: "GPT-Powered Responses", description: "Generate accurate, dynamic replies using advanced AI models.", icon: Sparkles },
  { title: "Multi-Channel Support", description: "Deploy across web, WhatsApp, Messenger, and more.", icon: Layers },
  { title: "Human Handoff", description: "Seamlessly transfer complex queries to live agents.", icon: UserCheck },
  { title: "Workflow Automation", description: "Trigger actions, tickets, and tasks automatically.", icon: Workflow },
  { title: "CRM Integration", description: "Sync leads and conversations with your CRM pipeline.", icon: Database },
  { title: "Analytics Dashboard", description: "Track performance, engagement, and conversion metrics.", icon: BarChart3 },
  { title: "Multilingual Support", description: "Serve global audiences in 50+ languages.", icon: Languages },
  { title: "Sentiment Analysis", description: "Detect customer mood and prioritize urgent cases.", icon: Smile },
  { title: "Custom Training", description: "Train bots on your data, docs, and brand voice.", icon: GraduationCap },
  { title: "Smart Recommendations", description: "Suggest products and solutions based on user behavior.", icon: Lightbulb },
  { title: "API Integrations", description: "Connect with your existing tools and platforms.", icon: Plug },
];

const ChatBotFeatures: React.FC = () => (
  <AnimatedSection as="section" className="cbot-section" aria-labelledby="cbot-features-title">
    <div className="cbot-container">
      <SectionHeader
        eyebrow="Powerful Features"
        title="Enterprise-Grade AI Capabilities"
        text="Everything you need to deliver intelligent, scalable, and conversion-focused chatbot experiences."
      />

      <div className="cbot-feature-grid">
        {features.map((feature, index) => {
          const Icon = feature.icon;
          return (
            <motion.article
              key={feature.title}
              className="cbot-glass-card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: index * 0.04 }}
              whileHover={{ y: -6, transition: { duration: 0.2 } }}
            >
              <div className="cbot-glass-card__icon">
                <Icon size={20} aria-hidden="true" />
              </div>
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
            </motion.article>
          );
        })}
      </div>
    </div>
  </AnimatedSection>
);

export default ChatBotFeatures;
